<script>
	import { onMount } from 'svelte';
	import ProductCard from '../components/ProductCard.svelte';
	import Sidebar from '../components/Sidebar.svelte';

	const ELASTICSEARCH_ENDPOINT =
		'https://0db10ac661ec48789716bd8b37fe2cf5.europe-west1.gcp.cloud.es.io';
	const API_KEY = 'RzJSd0g1WUJYamFFUWtoTTV4b2M6R1IxaVc5TnpRbEM4a3VpUFc2eV9EUQ==';
	const INDEX_NAME = 'abc-school-supplies';
	export const currentClient = 'abc-school-supplies';

	// State management
	let products = [];
	let filteredProducts = [];
	let selectedProducts = [];
	let amplifiedProducts = [];
	let categories = [];

	let searchQuery = '';
	let activeCategory = null;

	let activeView = 'all';
	let isLoading = true;
	let isLoadingMore = false;
	let totalProducts = 0;
	let currentPage = 1;
	let productsPerPage = 50;
	let hasMoreProducts = true;
	let error = null;
	let initialLoadComplete = false;

	let prevSearchQuery = '';
	let prevActiveCategory = null;
	let prevActiveView = 'all';

	// Modal state
	let isModalOpen = false;

	async function fetchProducts(page = 1, size = productsPerPage, append = false) {
		if (page === 1) {
			isLoading = true;
		} else {
			isLoadingMore = true;
		}
		error = null;

		try {
			// Build the Elasticsearch query
			const query = {
				from: (page - 1) * size,
				size: size,
				query: {
					bool: {
						must: searchQuery
							? [
									{
										multi_match: {
											query: searchQuery,
											fields: ['title^3', 'description_tokens', 'category_list', 'sku']
										}
									}
							  ]
							: [{ match_all: {} }],
						filter: []
					}
				},
				sort: [{ views_sort: 'desc' }],
				aggs: {
					count_of_category_list: {
						terms: { field: 'category_list.keyword', size: 10000 }
					}
				}
			};

			if (activeCategory) {
				query.query.bool.filter.push({
					term: { 'category_list.keyword': activeCategory }
				});
			}

			if (activeView === 'amplified') {
				// placeholder
			}

			if (activeView === 'on_sale') {
				query.query.bool.filter.push({
					term: { on_sale: true }
				});
			}

			const response = await fetch(`${ELASTICSEARCH_ENDPOINT}/${INDEX_NAME}/_search`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `ApiKey ${API_KEY}`
				},
				body: JSON.stringify(query)
			});

			if (!response.ok) {
				throw new Error(`Elasticsearch request failed: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();

			const fetchedProducts = data.hits.hits.map((hit) => ({
				...hit._source,
				_id: hit._id,
				amplified: false
			}));

			const fetchedCategories = data.aggregations.count_of_category_list.buckets.map((bucket) => ({
				name: bucket.key,
				count: bucket.doc_count
			}));

			if (append) {
				products = [...products, ...fetchedProducts];
			} else {
				products = fetchedProducts;
			}
			categories = fetchedCategories;

			totalProducts = data.hits.total.value;
			hasMoreProducts = products.length < totalProducts;

			if (activeView === 'amplified') {
				filteredProducts = products.filter((product) => product.amplified);
			} else {
				filteredProducts = [...products];
			}

			filteredProducts = filteredProducts.map((product) => {
				const amplifiedProduct = amplifiedProducts.find((p) => p.id === product.id);
				return amplifiedProduct ? { ...product, amplified: true } : product;
			});
		} catch (err) {
			console.error('Error fetching products:', err);
			error = err.message;
			filteredProducts = [];
		} finally {
			isLoading = false;
			isLoadingMore = false;
		}
	}

	onMount(() => {
		fetchProducts().finally(() => {
			prevSearchQuery = searchQuery;
			prevActiveCategory = activeCategory;
			prevActiveView = activeView;
			initialLoadComplete = true;
		});
		localStorage.setItem('shopboxBeta', 'true');
		var sbid = INDEX_NAME;
		var t = document.createElement('shopbox-app-v2'),
			s = document.createElement('script');
		t.setAttribute('cid', sbid);
		s.type = 'text/javascript';
		s.async = !0;
		s.src = 'https://widget.shopbox.ai/js/app.js';
		document.getElementsByTagName('body')[0].append(t);
		document.getElementsByTagName('head')[0].append(s);
	});

	$: {
		if (initialLoadComplete) {
			let queryChanged = searchQuery !== prevSearchQuery;
			let categoryChanged = activeCategory !== prevActiveCategory;
			let viewChanged = activeView !== prevActiveView;

			if (queryChanged || categoryChanged || viewChanged) {
				prevSearchQuery = searchQuery;
				prevActiveCategory = activeCategory;
				prevActiveView = activeView;

				currentPage = 1;
				fetchProducts(currentPage);
			}
		}
	}

	function toggleProductSelection(product) {
		const index = selectedProducts.findIndex((p) => p.id === product.id);
		if (index === -1) {
			selectedProducts = [...selectedProducts, product];
		} else {
			selectedProducts = selectedProducts.filter((p) => p.id !== product.id);
		}
	}

	function selectAllVisible() {
		if (selectedProducts.length === filteredProducts.length) {
			selectedProducts = [];
		} else {
			selectedProducts = [...filteredProducts];
		}
	}

	function openActionsModal() {
		isModalOpen = true;
	}

	function closeModal() {
		isModalOpen = false;
	}

	function amplifySelectedProducts() {
		products = products.map((product) => {
			if (selectedProducts.some((p) => p.id === product.id)) {
				return { ...product, amplified: true };
			}
			return product;
		});

		filteredProducts = filteredProducts.map((product) => {
			if (selectedProducts.some((p) => p.id === product.id)) {
				return { ...product, amplified: true };
			}
			return product;
		});

		amplifiedProducts = products.filter((p) => p.amplified);
		selectedProducts = [];
		closeModal();
	}

	function removeSelectedProduct(product) {
		selectedProducts = selectedProducts.filter((p) => p.id !== product.id);
	}

	function removeAmplifyFromSelected() {
		products = products.map((product) => {
			if (selectedProducts.some((p) => p.id === product.id)) {
				return { ...product, amplified: false };
			}
			return product;
		});

		filteredProducts = filteredProducts.map((product) => {
			if (selectedProducts.some((p) => p.id === product.id)) {
				return { ...product, amplified: false };
			}
			return product;
		});

		amplifiedProducts = products.filter((p) => p.amplified);
		selectedProducts = [];
		closeModal();
	}

	function markOutOfRecommenderEngine() {
		selectedProducts = [];
		closeModal();
	}

	function markOutOfStock() {
		selectedProducts = [];
		closeModal();
	}

	function setActiveCategory(categoryName) {
		activeCategory = activeCategory === categoryName ? null : categoryName;
		selectedProducts = [];
	}

	function setActiveView(view) {
		activeView = view;
		selectedProducts = [];
	}

	function isProductSelected(product) {
		return selectedProducts.some((p) => p.id === product.id);
	}

	function formatCurrency(amount) {
		return new Intl.NumberFormat('en-IE', {
			style: 'currency',
			currency: 'EUR'
		}).format(amount);
	}

	function getDiscountPercentage(price, regularPrice) {
		if (!regularPrice || !price) return 0;
		return Math.round(((regularPrice - price) / regularPrice) * 100);
	}

	function loadMore() {
		if (!isLoadingMore && hasMoreProducts) {
			currentPage += 1;
			fetchProducts(currentPage, productsPerPage, true);
		}
	}

	function handleLoadMore(event) {
		event.preventDefault();
		loadMore();
	}
</script>

<main class="page-main shop-loop-head">
	<div class="page-container">
		<div class="layout-container">
			<Sidebar
				{isLoading}
				{categories}
				{activeCategory}
				{activeView}
				{amplifiedProducts}
				on:selectView={(event) => setActiveView(event.detail)}
				on:selectCategory={(event) => setActiveCategory(event.detail)}
			/>

			<div class="main-content">
				<div class="toolbar-section">
					<div class="toolbar-flex-container">
						<div class="search-container">
							<input
								type="text"
								placeholder="Search products..."
								class="search-input"
								bind:value={searchQuery}
							/>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="search-icon"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
								/>
							</svg>
						</div>

						<div class="action-buttons">
							<button class="action-button select-all-button" on:click={selectAllVisible}>
								{selectedProducts.length === filteredProducts.length
									? 'Deselect All'
									: 'Select All'}
							</button>

							<button
								class="action-button action-menu-button"
								disabled={selectedProducts.length === 0}
								on:click={openActionsModal}
							>
								Actions
							</button>
						</div>
					</div>

					{#if selectedProducts.length > 0}
						<div class="selected-info">
							<p class="selected-count-text">
								{selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
							</p>
							{#if activeCategory}
								<!-- content here -->
								<button
									style="display:inline-flex;"
									class="action-button select-all-button"
									on:click={selectAllVisible}
								>
									Select all in {activeCategory}
								</button>
							{/if}
						</div>
					{/if}
				</div>

				<div class="products-section">
					<h2 class="products-heading">
						{activeView === 'all'
							? 'All Products'
							: activeView === 'amplified'
							? 'Amplified Products'
							: 'On Sale Products'}
						{activeCategory ? ` in ${activeCategory}` : ''}
						<span class="products-count-details">
							{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
							{totalProducts > filteredProducts.length ? ` (of ${totalProducts} total)` : ''}
						</span>
					</h2>

					{#if error}
						<div class="message-container error-message">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="message-icon error-icon"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
							<h3 class="message-title">Error loading products</h3>
							<p class="message-text error-text">{error}</p>
						</div>
					{:else if isLoading && filteredProducts.length === 0}
						<div class="product-grid loading-grid">
							{#each Array(8) as _}
								<div class="product-placeholder" />
							{/each}
						</div>
					{:else if filteredProducts.length === 0}
						<div class="message-container empty-message">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="message-icon empty-icon"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<h3 class="message-title">No products found</h3>
							<p class="message-text empty-text">Try adjusting your search or filters</p>
						</div>
					{:else}
						<div class="product-grid">
							{#each filteredProducts as product (product.id)}
								<ProductCard
									{product}
									isSelected={isProductSelected(product)}
									{formatCurrency}
									{getDiscountPercentage}
									on:toggleSelect={(event) => toggleProductSelection(event.detail)}
								/>
							{/each}
						</div>
					{/if}

					{#if filteredProducts.length > 0 && !isLoading && hasMoreProducts}
						<div class="load-more-container">
							<button class="load-more-button" on:click={handleLoadMore} disabled={isLoadingMore}>
								{#if isLoadingMore}
									<svg
										class="loading-spinner"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											class="spinner-circle"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											stroke-width="4"
										/>
										<path
											class="spinner-path"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
									Loading more...
								{:else}
									Load more products
								{/if}
							</button>
						</div>
					{/if}
				</div>
			</div>
		</div>
	</div>

	{#if isModalOpen}
		<div class="modal-overlay" on:click={closeModal}>
			<div class="modal-container" on:click|stopPropagation>
				<div class="modal-header">
					<h3 class="modal-title">Actions for Selected Products</h3>
					<button class="modal-close-button" on:click={closeModal}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							class="modal-close-icon"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>
				<div class="modal-body">
					<p class="modal-description">
						Select an action to apply to {selectedProducts.length} selected product{selectedProducts.length !==
						1
							? 's'
							: ''}:
					</p>
					<ul class="selected-products-list">
						{#each selectedProducts as p}
							<li>
								{p.title}
								<button class="modal-close-button" on:click={() => removeSelectedProduct(p)}>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										class="modal-close-icon"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</li>
						{/each}
					</ul>

					<div class="modal-actions-grid">
						<button class="modal-action-button" on:click={amplifySelectedProducts}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="modal-action-icon"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
								/>
							</svg>
							<span class="modal-action-text">Amplify Products</span>
						</button>

						<button class="modal-action-button" on:click={markOutOfRecommenderEngine}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="modal-action-icon"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
								/>
							</svg>
							<span class="modal-action-text">Remove from Recommender</span>
						</button>

						<button class="modal-action-button" on:click={markOutOfStock}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="modal-action-icon"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M20 12H4"
								/>
							</svg>
							<span class="modal-action-text">Mark as Out of Stock</span>
						</button>

						<button class="modal-action-button" on:click={removeAmplifyFromSelected}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								class="modal-action-icon"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
								/>
							</svg>
							<span class="modal-action-text">Remove Amplify</span>
						</button>
					</div>
				</div>
				<div class="modal-footer">
					<button class="modal-cancel-button" on:click={closeModal}>Cancel</button>
				</div>
			</div>
		</div>
	{/if}
</main>

<style>
	:root {
		--white: #ffffff;
		--accent: #000;
		--grey-50: #f9fafb;
		--grey-200: #e5e7eb;
		--grey-600: #4b5563;

		--error: #ef4444;

		--shadow-focus: 0 0 0 2px var(--grey-200);
		--shadow-focus-loadmore: 0 0 0 2px var(--white), 0 0 0 4px var(--accent);
		--shadow-focus-sidebar: 0 0 0 1px var(--accent);
	}

	.page-main {
		min-height: calc(100vh - 50px);
	}
	.page-container {
		margin-left: auto;
		margin-right: auto;
		padding: 2rem 1rem;
	}

	.layout-container {
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
		align-items: flex-start;
	}
	@media (min-width: 1024px) {
		.layout-container {
			flex-direction: row;
		}
	}
	.main-content {
		flex: 1 1 0%;
	}

	.toolbar-section {
		position: sticky;
		top: calc(1em + 50px);
		z-index: 9999;

		margin-bottom: 1rem;
		border-radius: 0.5rem;
		background-color: var(--white);
		padding: 1rem;
		box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
	}
	.toolbar-flex-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
	@media (min-width: 640px) {
		.toolbar-flex-container {
			flex-direction: row;
		}
	}

	.search-container {
		position: relative;
		flex: 1 1 0%;
	}
	.search-input {
		width: 100%;
		border-radius: 0.375rem;
		border: 1px solid var(--grey-200);
		padding: 0.5rem 1rem 0.5rem 2.5rem;
		outline: none;
		transition: box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out;
	}
	.search-input:focus {
		border-color: var(--accent);
		box-shadow: var(--shadow-focus);
	}
	.search-icon {
		position: absolute;
		top: 0.625rem;
		left: 0.75rem;
		height: 1.25rem;
		width: 1.25rem;
		color: var(--grey-200);
	}

	.action-buttons {
		display: flex;
		gap: 0.5rem;
	}
	.action-button {
		display: flex;
		align-items: center;
		border-radius: 0.375rem;
		padding: 0.5rem 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 500;
		border: none;
		cursor: pointer;
		transition: background-color 0.15s ease-in-out;
	}
	.action-button:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
	.select-all-button {
		background-color: var(--grey-200);
		color: var(--grey-600);
	}
	.select-all-button:hover:not(:disabled) {
		background-color: var(--grey-200);
	}
	.action-menu-button {
		background-color: var(--accent);
		color: var(--white);
	}
	.action-menu-button:hover:not(:disabled) {
		background-color: var(--grey-600);
	}
	.remove-amplify-button {
		background-color: var(--error);
		color: var(--white);
	}
	.remove-amplify-button:hover:not(:disabled) {
		background-color: var(--grey-600);
	}

	.selected-info {
		margin-top: 0.75rem;
		border-top: 1px solid var(--grey-200);
		padding-top: 0.75rem;
		display: flex;
		gap: 1rem;
		align-items: center;
	}
	.selected-count-text {
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--grey-600);
	}

	.products-section {
		border-radius: 0.5rem;
		background-color: var(--white);
		padding: 1rem;
		box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
	}
	.products-heading {
		margin-bottom: 1rem;
		font-size: 1.125rem;
		line-height: 1.75rem;
		font-weight: 600;
	}
	.products-count-details {
		margin-left: 0.5rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 400;
		color: var(--grey-600);
	}

	.message-container {
		padding: 3rem 0;
		text-align: center;
	}
	.message-icon {
		margin-left: auto;
		margin-right: auto;
		margin-bottom: 1rem;
		height: 3rem;
		width: 3rem;
	}
	.error-icon {
		color: var(--error);
	}
	.empty-icon {
		color: var(--grey-200);
	}
	.message-title {
		margin-bottom: 0.25rem;
		font-size: 1.125rem;
		line-height: 1.75rem;
		font-weight: 500;
		color: var(--grey-600);
	}
	.message-text {
		font-size: 1rem;
		line-height: 1.5rem;
	}
	.error-text {
		color: var(--error);
	}
	.empty-text {
		color: var(--grey-600);
	}

	.product-grid {
		display: grid;
		gap: 1rem;
		grid-template-columns: repeat(1, minmax(0, 1fr));
	}
	@media (min-width: 640px) {
		.product-grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	@media (min-width: 1024px) {
		.product-grid {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}

	.product-placeholder {
		height: 16rem;
		border-radius: 0.5rem;
		background-color: var(--grey-200);
		padding: 1rem;
	}

	.load-more-container {
		margin-top: 1.5rem;
		display: flex;
		justify-content: center;
	}
	.load-more-button {
		display: inline-flex;
		align-items: center;
		border-radius: 0.375rem;
		border: 1px solid var(--grey-200);
		background-color: var(--white);
		padding: 0.5rem 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 500;
		color: var(--grey-600);
		box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
		cursor: pointer;
		transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
		outline: none;
	}
	.load-more-button:hover:not(:disabled) {
		background-color: var(--grey-50);
	}
	.load-more-button:focus {
		box-shadow: var(--shadow-focus-loadmore);
	}
	.load-more-button:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}

	.loading-spinner {
		margin-right: 0.5rem;
		margin-left: -0.25rem;
		height: 1rem;
		width: 1rem;
		color: var(--grey-600);
		animation: spin 1s linear infinite;
	}
	.spinner-circle {
		opacity: 0.25;
	}
	.spinner-path {
		opacity: 0.75;
	}

	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

	/* Modal styles */
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 10000;
	}

	.modal-container {
		background-color: var(--white);
		border-radius: 0.5rem;
		width: 90%;
		max-width: 500px;
		max-height: 90vh;
		overflow-y: auto;
		box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
	}

	.modal-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1rem;
		border-bottom: 1px solid var(--grey-200);
	}

	.modal-title {
		margin: 0;
		font-size: 1.125rem;
		font-weight: 600;
	}

	.modal-close-button {
		background: transparent;
		border: none;
		cursor: pointer;
		padding: 0.25rem;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 0.25rem;
	}

	.modal-close-button:hover {
		background-color: var(--grey-50);
	}

	.modal-close-icon {
		height: 1.25rem;
		width: 1.25rem;
		color: var(--grey-600);
	}

	.modal-body {
		padding: 1rem;
	}

	.modal-description {
		margin-bottom: 1rem;
		color: var(--grey-600);
	}

	.modal-actions-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 1rem;
	}

	.modal-action-button {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 1rem;
		border: 1px solid var(--grey-200);
		border-radius: 0.5rem;
		background-color: var(--white);
		cursor: pointer;
		transition: background-color 0.15s ease-in-out;
	}

	.modal-action-button:hover {
		background-color: var(--grey-50);
	}

	.modal-action-icon {
		height: 2rem;
		width: 2rem;
		color: var(--accent);
		margin-bottom: 0.5rem;
	}

	.modal-action-text {
		text-align: center;
		font-size: 0.875rem;
		font-weight: 500;
	}

	.modal-footer {
		display: flex;
		justify-content: flex-end;
		padding: 1rem;
		border-top: 1px solid var(--grey-200);
	}

	.modal-cancel-button {
		padding: 0.5rem 1rem;
		border: 1px solid var(--grey-200);
		border-radius: 0.375rem;
		background-color: var(--white);
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--grey-600);
		cursor: pointer;
		transition: background-color 0.15s ease-in-out;
	}

	.modal-cancel-button:hover {
		background-color: var(--grey-50);
	}

	.selected-products-list {
		padding: 0.5rem;
		border: 1px solid var(--grey-200);
		border-radius: 0.375rem;
		background-color: var(--white);
		font-size: 0.875rem;
		height: 200px;
		overflow: scroll;
		margin-bottom: 0.5em;
	}
	.selected-products-list li {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.25rem 0.5rem;
		border-radius: 0.375rem;
		background-color: var(--grey-50);
		margin-bottom: 4px;
	}
</style>
